import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Download, XCircle } from "lucide-react";
import { API } from "../lib/api";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/shop/status/${sid}`);
        setOrder(data);
        if (data.payment_status === "paid") return setState("paid");
        if (data.payment_status === "expired") return setState("failed");
      } catch {}
      if (tries >= 10) return setState("timeout");
      setTimeout(poll, 2000);
    };
    sid ? poll() : setState("failed");
    return () => { stop = true; };
  }, [sid]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="card-surface p-10 max-w-lg w-full text-center" data-testid="order-success-page">
        {state === "checking" && (<><Loader2 className="mx-auto text-teal-400 animate-spin mb-4" size={40} /><h1 className="font-display text-3xl">Confirming your payment…</h1></>)}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-teal-400 mb-4" size={48} />
            <h1 className="font-display text-3xl mb-3" data-testid="order-paid-title">Thank you.</h1>
            {order.kind === "gift" ? (
              <p className="text-neutral-300 text-sm" data-testid="order-gift-msg">
                Your gift is on its way. We've emailed {order.recipient_name} a link to pick their own time, and sent your receipt to {order.email}.
              </p>
            ) : order.kind === "session" ? (
              <p className="text-neutral-300 text-sm" data-testid="order-session-msg">
                Your 30-minute session is booked for {order.slot_label}. I'll email {order.email} a Google Meet link before our call.
              </p>
            ) : (
              <>
                <p className="text-neutral-400 text-sm mb-6">Your toolkits are below. We've also emailed the links to {order.email}.</p>
                <div className="space-y-2 text-left">
                  {order.downloads.map((d) => (
                    <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}${d.pdf}`} target="_blank" rel="noopener noreferrer" data-testid={`order-download-${d.id}`}
                      className="flex items-center justify-between rounded-xl border border-white/10 hover:border-teal-400/50 px-4 py-3 text-sm text-neutral-100">
                      {d.title} <Download size={14} className="text-teal-300" />
                    </a>
                  ))}
                </div>
                {order.rate_token && (
                  <Link to={`/rate/${order.rate_token}`} className="block mt-5 text-xs text-amber-300 hover:text-amber-200" data-testid="order-rate-link">
                    Already know how it feels? Rate your toolkits
                  </Link>
                )}
              </>
            )}
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="mx-auto text-neutral-500 mb-4" size={44} />
            <h1 className="font-display text-3xl mb-2" data-testid="order-failed-title">
              {state === "failed" ? "Payment didn't go through" : "Still confirming"}
            </h1>
            <p className="text-neutral-400 text-sm">
              {state === "failed" ? "No charge was made." : "Check your email in a few minutes. Your receipt and links will arrive there."}
            </p>
          </>
        )}
        <Link to="/" className="inline-block mt-8 text-sm text-teal-300 hover:text-teal-200" data-testid="order-back-home">Back to Why We Recover</Link>
      </div>
    </div>
  );
}
